import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { MESSENGER_URL } from "../../config/env";
import { applyAuthHeaders } from "../../utils/authHeaders";
import { createBaseQueryWithReauth } from "./baseQueryWithReauth";

// telegram status response types
export interface TelegramStatusData {
  connected: boolean;
  telegramUsername?: string | null;
  telegramFirstName?: string | null;
  globalMute?: boolean;
  connectedAt?: string | null;
  lastActiveAt?: string | null;
}

export interface TelegramStatusResponse {
  success: boolean;
  data?: TelegramStatusData;
  error?: string;
}

export interface VapidPublicKeyResponse {
  success: boolean;
  data?: { publicKey: string; configured: boolean };
  error?: string;
}

export interface PushDeviceRow {
  id: string;
  userAgent: string | null;
  addedAt: string | null;
  isThisDevice: boolean;
}

export interface PushStatusResponse {
  success: boolean;
  data?: {
    subscriptionCount: number;
    configured: boolean;
    devices?: PushDeviceRow[];
  };
  error?: string;
}

export interface PushTestResponse {
  success: boolean;
  data?: { sent: boolean };
  error?: string;
}

export interface PushSubscribeBody {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

export interface PushSubscribeResponse {
  success: boolean;
  data?: { subscribed: boolean };
  error?: string;
}

export interface PushUnsubscribeResponse {
  success: boolean;
  data?: { removed: boolean };
  error?: string;
}

// base query for messenger service
const baseQuery = fetchBaseQuery({
  baseUrl: MESSENGER_URL,
  // credentials: 'include', // IMPORTANT: sends HttpOnly cookie for refresh token
  prepareHeaders: (headers) => applyAuthHeaders(headers),
});

// wrap with reauth logic (proactive + reactive token refresh)
const baseQueryWithReauth = createBaseQueryWithReauth({
  baseQuery,
});

export const messengerApi = createApi({
  reducerPath: "messengerApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getTelegramStatus: builder.query<TelegramStatusResponse, void>({
      query: () => ({
        url: "/user/telegram-status",
        method: "GET",
      }),
    }),
    getVapidPublicKey: builder.query<VapidPublicKeyResponse, void>({
      query: () => ({
        url: "/api/user/push/vapid-public-key",
        method: "GET",
      }),
    }),
    getPushStatus: builder.query<PushStatusResponse, { endpoint?: string | null }>({
      query: ({ endpoint }) => {
        const base = "/api/user/push/status";
        if (endpoint) {
          return {
            url: `${base}?endpoint=${encodeURIComponent(endpoint)}`,
            method: "GET",
          };
        }
        return { url: base, method: "GET" };
      },
    }),
    subscribePush: builder.mutation<PushSubscribeResponse, PushSubscribeBody>({
      query: (body) => ({
        url: "/api/user/push/subscribe",
        method: "POST",
        body,
      }),
    }),
    unsubscribePush: builder.mutation<
      PushUnsubscribeResponse,
      { endpoint?: string; subscriptionId?: string }
    >({
      query: (body) => ({
        url: "/api/user/push/unsubscribe",
        method: "POST",
        body,
      }),
    }),
    sendTestPush: builder.mutation<PushTestResponse, void>({
      query: () => ({
        url: "/api/user/push/test",
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetTelegramStatusQuery,
  useGetVapidPublicKeyQuery,
  useGetPushStatusQuery,
  useSubscribePushMutation,
  useUnsubscribePushMutation,
  useSendTestPushMutation,
} = messengerApi;
